/**
 * Temu 发布记录本地存储（按店铺隔离）。
 * 列表展示态：核价单缓存 + 平台货品生命周期（bg.glo.product.search）。
 */

import {
  applyDerivedPublishStatuses,
  buildSkuReviewStatusMap,
  PUBLISH_RECORD_STATUS,
} from '@/temu-platform/utils/temuPublishRecordStatus';
import { applyLifecycleToPublishRecords, collectPublishRecordSkuIds } from '@/temu-platform/utils/temuProductLifecycle';
import { nameOfSiteId } from '@/temu-platform/utils/temuDeploymentRegions';
import { nowBeijingOffsetIso } from '@/temu-platform/utils/formatBeijingDateTime';

const IDB_NAME = 'ipe-temu-publish-records';
const IDB_STORE = 'records';
const IDB_VERSION = 1;
const INDEX_PREFIX = 'ipe_temu_publish_records_index_';

function indexKey(shopId) {
  return `${INDEX_PREFIX}${String(shopId || '0')}`;
}

function makeId() {
  return `tpr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readIndex(shopId) {
  try {
    const raw = localStorage.getItem(indexKey(shopId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** 供 lifecycle 查询：当前店铺发布记录里的 productSkuId */
export function listTemuPublishRecordSkuIds(shopId) {
  return collectPublishRecordSkuIds(readIndex(shopId));
}

/**
 * 从发布记录收集可供 bg.glo.product.search 的 productSkuId。
 * 平台接口只认 productSkuIdList；无 SKU 时返回空（不应用 SPU/SKC 冒充）。
 */
export function collectPublishRecordLifecycleQueryIds(shopId) {
  const index = readIndex(shopId);
  if (!index.length) {
    return { productSkuIds: [], productIds: [], skcIds: [], recordCount: 0 };
  }
  const productSkuIds = collectPublishRecordSkuIds(index);
  const productIds = new Set();
  const skcIds = new Set();
  index.forEach((row) => {
    const pid = Number(row?.platformSpuName ?? row?.productId);
    if (Number.isSafeInteger(pid) && pid > 0) productIds.add(pid);
    (Array.isArray(row?.skcItems) ? row.skcItems : []).forEach((skc) => {
      const sid = Number(skc?.skcName ?? skc?.productSkcId ?? skc?.skcId);
      if (Number.isSafeInteger(sid) && sid > 0) skcIds.add(sid);
    });
  });
  return {
    productSkuIds: productSkuIds.slice(0, 100),
    productIds: [...productIds],
    skcIds: [...skcIds],
    recordCount: index.length,
  };
}

const LIFECYCLE_SYNCED_AT_PREFIX = 'ipe_temu_publish_lifecycle_synced_at_';
const LIFECYCLE_CACHE_PREFIX = 'ipe_temu_publish_lifecycle_cache_';

export function getTemuPublishLifecycleSyncedAt(shopId) {
  try {
    return localStorage.getItem(`${LIFECYCLE_SYNCED_AT_PREFIX}${String(shopId || '')}`) || '';
  } catch {
    return '';
  }
}

export function setTemuPublishLifecycleSyncedAt(shopId, iso = nowBeijingOffsetIso()) {
  try {
    localStorage.setItem(`${LIFECYCLE_SYNCED_AT_PREFIX}${String(shopId || '')}`, String(iso || ''));
  } catch {
    /* ignore */
  }
}

/** 上次手动拉取的生命周期行（列表展示用，打开页不再打平台） */
export function getTemuPublishLifecycleCache(shopId) {
  try {
    const raw = localStorage.getItem(`${LIFECYCLE_CACHE_PREFIX}${String(shopId || '')}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function setTemuPublishLifecycleCache(shopId, items = []) {
  try {
    const key = `${LIFECYCLE_CACHE_PREFIX}${String(shopId || '')}`;
    const list = Array.isArray(items) ? items : [];
    if (!list.length) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

function writeIndex(shopId, items) {
  try {
    localStorage.setItem(indexKey(shopId), JSON.stringify(items));
  } catch {
    /* quota */
  }
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onerror = () => reject(req.error || new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        const store = db.createObjectStore(IDB_STORE, { keyPath: 'id' });
        store.createIndex('shopId', 'shopId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function idbPut(record) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.oncomplete = () => {
      db.close();
      resolve(record);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('indexedDB put failed'));
    };
    tx.objectStore(IDB_STORE).put(record);
  }));
}

function idbGet(id) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(String(id));
    req.onsuccess = () => {
      db.close();
      resolve(req.result || null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error || new Error('indexedDB get failed'));
    };
  }));
}

function idbDelete(ids) {
  const idList = (Array.isArray(ids) ? ids : [ids]).map(String).filter(Boolean);
  if (!idList.length) return Promise.resolve(0);
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    let deleted = 0;
    tx.oncomplete = () => {
      db.close();
      resolve(deleted);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error('indexedDB delete failed'));
    };
    const store = tx.objectStore(IDB_STORE);
    idList.forEach((id) => {
      store.delete(id);
      deleted += 1;
    });
  }));
}

function imageUrlOf(image) {
  if (!image) return '';
  return String(
    image.temuUrl
    || image.publicUrl
    || image.ipeUrl
    || image.previewUrl
    || image.url
    || '',
  ).trim();
}

function firstSupplierCode(form) {
  const fromSkc = (form?.skcList || [])
    .map((skc) => String(skc?.supplierCode || '').trim())
    .find(Boolean);
  return fromSkc || String(form?.supplierCode || '').trim();
}

/**
 * 复色记录里表单 SKC = 平台已有 SKC + 本次新增 SKC，而平台只回新增 SKC 的 id，
 * 直接按下标对齐会把新增 SKC 挂到已有颜色上；只有数量一致、或空白 SKC 数量一致时才对齐。
 */
function resolvePlatformSkcForFormSkc(formSkcs, platformByIndex, index) {
  if (!platformByIndex.length) return null;
  const skc = formSkcs[index] || {};
  if (formSkcs.length === platformByIndex.length) {
    return platformByIndex[index] || null;
  }
  const blankIndexes = formSkcs
    .map((row, i) => (String(row?.skcName || '').trim() ? -1 : i))
    .filter((i) => i >= 0);
  if (blankIndexes.length === platformByIndex.length) {
    const at = blankIndexes.indexOf(index);
    return at >= 0 ? (platformByIndex[at] || null) : null;
  }
  // 兜底：按商家货号（extCode）匹配平台 SKC
  const code = String(skc?.supplierCode || '').trim().toUpperCase();
  if (!code) return null;
  return platformByIndex.find((plat) => (
    String(plat?.extCode || plat?.supplierCode || '').trim().toUpperCase() === code
  )) || null;
}

function buildSkcItemsFromForm(form = {}, platformSkcList = []) {
  const formSkcs = Array.isArray(form.skcList) ? form.skcList : [];
  const platformByIndex = Array.isArray(platformSkcList) ? platformSkcList : [];
  if (formSkcs.length) {
    return formSkcs.map((skc, index) => {
      const plat = resolvePlatformSkcForFormSkc(formSkcs, platformByIndex, index) || {};
      const ids = collectSkuIdsFromSkcNode(plat);
      const formSkuIds = (Array.isArray(skc.skuList) ? skc.skuList : [])
        .map((s) => s?.productSkuId ?? s?.skuId)
        .map((v) => Number(v))
        .filter((n) => Number.isSafeInteger(n) && n > 0);
      const finalIds = ids.length ? ids : formSkuIds;
      return {
        colorLabel: skc.colorLabel || skc.colorExtraValue || '',
        // SKC 只认平台返回值，绝不拿商家货号冒充
        skcName: String(plat.productSkcId || plat.skcId || skc.skcName || '').trim(),
        productSkcId: plat.productSkcId ?? plat.skcId ?? null,
        productSkuIds: finalIds,
        imageUrl: imageUrlOf(skc.mainImage),
        merchantSkus: finalIds.map((id) => ({ productSkuId: id })),
        supplierCode: String(skc.supplierCode || '').trim(),
        status: PUBLISH_RECORD_STATUS.PENDING_REVIEW,
        statusLabel: PUBLISH_RECORD_STATUS.PENDING_REVIEW,
      };
    });
  }
  return platformByIndex.map((plat) => {
    const ids = collectSkuIdsFromSkcNode(plat);
    return {
      colorLabel: '',
      skcName: String(plat.productSkcId || plat.skcId || '').trim(),
      productSkcId: plat.productSkcId ?? plat.skcId ?? null,
      productSkuIds: ids,
      imageUrl: '',
      merchantSkus: ids.map((id) => ({ productSkuId: id })),
      supplierCode: String(plat.extCode || plat.supplierCode || '').trim(),
      status: PUBLISH_RECORD_STATUS.PENDING_REVIEW,
      statusLabel: PUBLISH_RECORD_STATUS.PENDING_REVIEW,
    };
  });
}

/** 平台发品响应里常见的 SKU 列表字段名 */
const PLATFORM_SKU_LIST_KEYS = [
  'productSkuList',
  'productSkuIdList',
  'productSkuIds',
  'skuList',
  'skuIdList',
  'skuInfoList',
  'productSkuReqs',
  'productSkuSummaries',
  'skus',
];

function unwrapPlatformResult(platformRes) {
  if (!platformRes || typeof platformRes !== 'object') return null;
  if (platformRes.result && typeof platformRes.result === 'object') return platformRes.result;
  if (platformRes.info && typeof platformRes.info === 'object'
    && (platformRes.info.productId != null
      || platformRes.info.productSkcList != null
      || platformRes.info.productSkuList != null
      || platformRes.info.skuInfoList != null)) {
    return platformRes.info;
  }
  return platformRes;
}

function extractSkuIdsFromPlatformNode(node, into) {
  if (node == null) return;
  if (typeof node === 'number' || typeof node === 'string') {
    const n = Number(node);
    if (Number.isSafeInteger(n) && n > 0) into.add(n);
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((item) => extractSkuIdsFromPlatformNode(item, into));
    return;
  }
  if (typeof node !== 'object') return;
  const direct = node.productSkuId ?? node.skuId ?? node.product_sku_id ?? node.sku_id;
  if (direct != null) extractSkuIdsFromPlatformNode(direct, into);
  PLATFORM_SKU_LIST_KEYS.forEach((key) => {
    if (node[key] != null) extractSkuIdsFromPlatformNode(node[key], into);
  });
}

/** 单个 SKC 节点下的 productSkuId 列表（兼容 IdList / 对象列表） */
function collectSkuIdsFromSkcNode(plat) {
  const ids = new Set();
  if (!plat || typeof plat !== 'object') return [];
  PLATFORM_SKU_LIST_KEYS.forEach((key) => {
    if (plat[key] != null) extractSkuIdsFromPlatformNode(plat[key], ids);
  });
  return [...ids];
}

/**
 * 从发品响应抽出「可按 SKC 分组」的 SKU 明细。
 * @returns {{ skcId: string, productSkuId: number, extCode: string }[]}
 */
function collectPlatformSkuEntries(platformRes) {
  const result = unwrapPlatformResult(platformRes);
  if (!result || typeof result !== 'object') return [];
  const out = [];
  const pushEntry = (raw, skcId = '') => {
    if (raw == null) return;
    if (typeof raw === 'number' || typeof raw === 'string') {
      const n = Number(raw);
      if (Number.isSafeInteger(n) && n > 0) {
        out.push({ skcId: String(skcId || ''), productSkuId: n, extCode: '' });
      }
      return;
    }
    if (typeof raw !== 'object') return;
    const id = Number(raw.productSkuId ?? raw.skuId ?? raw.product_sku_id ?? raw.sku_id);
    if (!(Number.isSafeInteger(id) && id > 0)) return;
    out.push({
      skcId: String(skcId || raw.productSkcId || raw.skcId || ''),
      productSkuId: id,
      extCode: String(raw.extCode || raw.outSkuSn || raw.skuSn || raw.supplierSku || '').trim(),
    });
  };
  const walkSkuBucket = (bucket, skcId = '') => {
    if (bucket == null) return;
    if (Array.isArray(bucket)) {
      bucket.forEach((item) => pushEntry(item, skcId));
      return;
    }
    pushEntry(bucket, skcId);
  };

  const skcList = Array.isArray(result.productSkcList) ? result.productSkcList : [];
  skcList.forEach((skc) => {
    const skcId = String(skc?.productSkcId ?? skc?.skcId ?? '').trim();
    PLATFORM_SKU_LIST_KEYS.forEach((key) => walkSkuBucket(skc?.[key], skcId));
  });
  PLATFORM_SKU_LIST_KEYS.forEach((key) => walkSkuBucket(result[key], ''));
  // 去重保序
  const seen = new Set();
  return out.filter((row) => {
    const key = `${row.skcId}:${row.productSkuId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function collectSkuIdsFromPlatformRes(platformRes) {
  return [...new Set(collectPlatformSkuEntries(platformRes).map((e) => e.productSkuId))];
}

/**
 * 把平台返回的 productSkuId 挂到各 SKC（优先按 SKC、再按 extCode、再按序）。
 */
function assignPlatformSkuIdsToSkcItems(skcItems = [], form = {}, platformRes = null) {
  const formSkcs = Array.isArray(form?.skcList) ? form.skcList : [];
  const entries = collectPlatformSkuEntries(platformRes);
  const flatIds = entries.map((e) => e.productSkuId);
  const bySkc = new Map();
  entries.forEach((e) => {
    if (!e.skcId) return;
    if (!bySkc.has(e.skcId)) bySkc.set(e.skcId, []);
    bySkc.get(e.skcId).push(e);
  });
  let flatCursor = 0;

  return (Array.isArray(skcItems) ? skcItems : []).map((skc, idx) => {
    const existing = (Array.isArray(skc?.productSkuIds) ? skc.productSkuIds : [])
      .map((v) => Number(v))
      .filter((n) => Number.isSafeInteger(n) && n > 0);
    if (existing.length) return skc;

    const skcKey = String(skc?.productSkcId || skc?.skcId || skc?.skcName || '').trim();
    let matched = skcKey && bySkc.has(skcKey) ? bySkc.get(skcKey) : null;
    if (!matched || !matched.length) {
      const formSkc = formSkcs[idx] || {};
      const skuRows = Array.isArray(formSkc.skuRows) && formSkc.skuRows.length
        ? formSkc.skuRows
        : (Array.isArray(formSkc.skuList) ? formSkc.skuList : []);
      const byExt = [];
      skuRows.forEach((row) => {
        const ext = String(row?.supplierSku || row?.extCode || '').trim();
        if (!ext) return;
        const hit = entries.find((e) => e.extCode && e.extCode === ext);
        if (hit) byExt.push(hit);
      });
      if (byExt.length) matched = byExt;
    }
    let ids = (matched || []).map((e) => e.productSkuId);
    if (!ids.length && flatIds.length) {
      const formSkc = formSkcs[idx] || {};
      const skuRows = Array.isArray(formSkc.skuRows) && formSkc.skuRows.length
        ? formSkc.skuRows
        : (Array.isArray(formSkc.skuList) ? formSkc.skuList : []);
      const need = Math.max(1, skuRows.length || 0);
      ids = flatIds.slice(flatCursor, flatCursor + need);
      if (!ids.length && idx === 0) ids = [...flatIds];
      flatCursor += ids.length || need;
    }
    if (!ids.length) return skc;
    return {
      ...skc,
      productSkuIds: ids,
      merchantSkus: ids.map((id) => ({ productSkuId: id })),
    };
  });
}

/** 已有 sitePriceLines 时，按序把 productSkuIds 补到 platformSkuId */
function attachPlatformSkuIdsToSitePriceLines(skc) {
  const ids = (Array.isArray(skc?.productSkuIds) ? skc.productSkuIds : [])
    .map((id) => String(id ?? '').trim())
    .filter(Boolean);
  const lines = Array.isArray(skc?.sitePriceLines) ? skc.sitePriceLines : [];
  if (!ids.length || !lines.length) return skc;
  const labelOrder = [];
  lines.forEach((line) => {
    const label = String(line?.skuLabel || '').trim() || 'SKU';
    if (!labelOrder.includes(label)) labelOrder.push(label);
  });
  const idByLabel = new Map(labelOrder.map((label, i) => [label, ids[i] || '']));
  return {
    ...skc,
    sitePriceLines: lines.map((line) => {
      if (line?.platformSkuId || line?.productSkuId) return line;
      const label = String(line?.skuLabel || '').trim() || 'SKU';
      const platformSkuId = idByLabel.get(label) || '';
      return platformSkuId ? { ...line, platformSkuId } : line;
    }),
  };
}

/** 站点 id / 文案 → 中文站名；已是中文则原样返回 */
export function resolveTemuSiteDisplayName(raw) {
  const text = String(raw ?? '').trim();
  if (!text || text === '—') return '';
  if (/[\u4e00-\u9fff]/.test(text)) return text;
  // 兼容「102 / 英国站」或逗号分隔
  if (text.includes('、') || text.includes(',') || text.includes('/')) {
    const parts = text.split(/[、,/]/).map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      return parts.map((p) => resolveTemuSiteDisplayName(p) || p).filter(Boolean).join('、');
    }
  }
  return nameOfSiteId(text) || text;
}

function resolveSiteLabels(form = {}) {
  const raw = [];
  if (Array.isArray(form.selectedSites) && form.selectedSites.length) {
    form.selectedSites.forEach((s) => {
      const t = String(s).trim();
      if (t) raw.push(t);
    });
  } else if (Array.isArray(form.siteIds) && form.siteIds.length) {
    form.siteIds.forEach((s) => {
      const t = String(s).trim();
      if (t) raw.push(t);
    });
  } else if (form.siteId != null && String(form.siteId).trim()) {
    raw.push(String(form.siteId).trim());
  }
  return raw.map((id) => resolveTemuSiteDisplayName(id) || id).filter(Boolean);
}

function formatSupplyPrice(price, currency) {
  if (price == null || price === '') return '';
  const n = Number(price);
  const text = Number.isFinite(n)
    ? (Math.abs(n - Math.round(n)) < 1e-9 ? String(Math.round(n)) : n.toFixed(2))
    : String(price).trim();
  if (!text) return '';
  const cur = String(currency || '').trim();
  return cur ? `${cur} ${text}` : text;
}

/**
 * 把发品表单里的站点 + 每 SKU 供货价落到 skcItems，供发布记录列表展示。
 */
export function enrichSkcItemsWithSitePrices(skcItems = [], form = {}) {
  const siteLabels = resolveSiteLabels(form);
  const siteText = siteLabels.join('、') || '';
  const currency = form?.currency || '';
  const formSkcs = Array.isArray(form?.skcList) ? form.skcList : [];
  return (Array.isArray(skcItems) ? skcItems : []).map((skc, idx) => {
    const formSkc = formSkcs[idx] || {};
    const skuRows = Array.isArray(formSkc.skuRows) && formSkc.skuRows.length
      ? formSkc.skuRows
      : (Array.isArray(formSkc.skuList) ? formSkc.skuList : []);
    const platformSkuIds = (Array.isArray(skc?.productSkuIds) ? skc.productSkuIds : [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean);
    const lines = [];
    if (skuRows.length) {
      skuRows.forEach((sku, si) => {
        const skuLabel = String(
          sku.sizeLabel || sku.supplierSku || sku.specName || `规格${si + 1}`,
        ).trim();
        const platformSkuId = String(
          sku.productSkuId ?? sku.skuId ?? platformSkuIds[si] ?? '',
        ).trim();
        const basePrice = sku.supplyPrice ?? sku.price ?? sku.supplierPriceYuan
          ?? form.supplyPrice ?? form.defaultPriceYuan;
        if (Array.isArray(sku.siteSupplierPrices) && sku.siteSupplierPrices.length) {
          sku.siteSupplierPrices.forEach((sp) => {
            const p = formatSupplyPrice(
              sp.supplierPriceYuan ?? sp.priceYuan ?? sp.supplierPrice ?? basePrice,
              sku.currency || currency,
            );
            lines.push({
              skuLabel,
              platformSkuId,
              siteText: resolveTemuSiteDisplayName(
                sp.siteName || sp.siteAbbr || sp.siteId || siteText || '',
              ) || siteText || '—',
              priceText: p || '—',
            });
          });
        } else {
          lines.push({
            skuLabel,
            platformSkuId,
            siteText: siteText || '—',
            priceText: formatSupplyPrice(basePrice, sku.currency || currency) || '—',
          });
        }
      });
    } else {
      const priceText = formatSupplyPrice(
        form.supplyPrice ?? form.defaultPriceYuan,
        currency,
      );
      if (platformSkuIds.length) {
        platformSkuIds.forEach((id) => {
          lines.push({
            skuLabel: '',
            platformSkuId: id,
            siteText: siteText || '—',
            priceText: priceText || '—',
          });
        });
      } else if (priceText || siteText) {
        lines.push({
          skuLabel: '',
          platformSkuId: '',
          siteText: siteText || '—',
          priceText: priceText || '—',
        });
      }
    }
    return {
      ...skc,
      siteLabels,
      sitePriceLines: lines,
    };
  });
}

function toListItem(record) {
  return {
    id: record.id,
    shopId: record.shopId,
    title: record.title || '',
    supplierCode: record.supplierCode || '',
    platformSpuName: record.platformSpuName || '',
    categoryLabel: record.categoryLabel || '',
    action: record.action || 'publish',
    source: record.source || 'wizard',
    typeLabel: record.typeLabel || (record.action === 'edit' ? '编辑' : '新增'),
    status: record.status || PUBLISH_RECORD_STATUS.PENDING_REVIEW,
    statusLabel: record.statusLabel || record.status || PUBLISH_RECORD_STATUS.PENDING_REVIEW,
    skcItems: Array.isArray(record.skcItems) ? record.skcItems : [],
    productSkuIds: Array.isArray(record.productSkuIds) ? record.productSkuIds : [],
    siteLabels: Array.isArray(record.siteLabels) ? record.siteLabels : [],
    draftId: record.draftId || null,
    remark: record.remark || '',
    createdAt: record.createdAt || null,
    publishedAt: record.publishedAt || null,
    finishedAt: record.finishedAt || null,
    updatedAt: record.updatedAt || null,
    canDelete: true,
    canRevoke: false,
  };
}

/**
 * 发品成功后写入本地发布记录（初始「待审核」）。
 */
export async function createTemuLocalPublishRecord(shopId, {
  form = {},
  platformRes = null,
  publishedSpu = '',
  draftId = null,
  action = 'publish',
  source = 'wizard',
} = {}) {
  const id = makeId();
  const now = nowBeijingOffsetIso();
  const result = unwrapPlatformResult(platformRes) || {};
  const platformSkcList = Array.isArray(result?.productSkcList)
    ? result.productSkcList
    : (Array.isArray(result?.productSkcIdList) ? result.productSkcIdList.map((v) => ({ productSkcId: v })) : []);
  let skcItems = buildSkcItemsFromForm(form, platformSkcList);
  skcItems = assignPlatformSkuIdsToSkcItems(skcItems, form, platformRes);
  skcItems = enrichSkcItemsWithSitePrices(skcItems, form).map(attachPlatformSkuIdsToSitePriceLines);
  const productSkuIds = [...new Set([
    ...skcItems.flatMap((s) => s.productSkuIds || []),
    ...collectSkuIdsFromPlatformRes(platformRes),
  ])];
  const siteLabels = resolveSiteLabels(form);
  const record = {
    id,
    shopId: String(shopId),
    title: String(form?.title || form?.productName || publishedSpu || '未命名商品').trim(),
    supplierCode: firstSupplierCode(form),
    platformSpuName: String(publishedSpu || result?.productId || form?.spuName || '').trim(),
    categoryLabel: String(form?.categoryLabel || form?.categoryPathLabel || '').trim(),
    action: action === 'edit' ? 'edit' : 'publish',
    source: String(source || 'wizard').trim() || 'wizard',
    typeLabel: action === 'edit' ? '编辑' : '新增',
    status: PUBLISH_RECORD_STATUS.PENDING_REVIEW,
    statusLabel: PUBLISH_RECORD_STATUS.PENDING_REVIEW,
    skcItems,
    productSkuIds,
    siteLabels,
    draftId: draftId != null ? String(draftId) : null,
    formSnapshot: form && typeof form === 'object' ? form : {},
    platformRaw: platformRes || null,
    remark: '',
    createdAt: now,
    publishedAt: now,
    finishedAt: null,
    updatedAt: now,
  };
  try {
    await idbPut(record);
  } catch {
    try {
      localStorage.setItem(`ipe_temu_publish_record_body_${id}`, JSON.stringify(record));
    } catch {
      /* ignore */
    }
  }
  const index = readIndex(shopId);
  index.unshift(toListItem(record));
  writeIndex(shopId, index.slice(0, 500));
  return toListItem(record);
}

/**
 * 用平台响应 / 表单快照补全已有发布记录（刊登任务回写缺 productSkuId 或 formSnapshot 时）。
 */
export async function enrichTemuLocalPublishRecord(shopId, recordId, {
  form = null,
  platformRes = null,
} = {}) {
  const full = await getTemuLocalPublishRecord(shopId, recordId);
  if (!full) return null;
  const nextForm = (form && typeof form === 'object' && Object.keys(form).length)
    ? form
    : (full.formSnapshot && typeof full.formSnapshot === 'object' ? full.formSnapshot : {});
  const nextPlatform = platformRes || full.platformRaw || null;
  const result = unwrapPlatformResult(nextPlatform) || {};
  const platformSkcList = Array.isArray(result?.productSkcList)
    ? result.productSkcList
    : (Array.isArray(result?.productSkcIdList)
      ? result.productSkcIdList.map((v) => ({ productSkcId: v }))
      : []);
  let skcItems = Array.isArray(full.skcItems) && full.skcItems.length
    ? full.skcItems
    : buildSkcItemsFromForm(nextForm, platformSkcList);
  if (platformSkcList.length && (!skcItems.length || skcItems.every((s) => !s?.productSkcId && !s?.skcName))) {
    skcItems = buildSkcItemsFromForm(nextForm, platformSkcList);
  }
  skcItems = assignPlatformSkuIdsToSkcItems(skcItems, nextForm, nextPlatform);
  skcItems = enrichSkcItemsWithSitePrices(skcItems, nextForm).map(attachPlatformSkuIdsToSitePriceLines);
  const productSkuIds = [...new Set([
    ...skcItems.flatMap((s) => s.productSkuIds || []),
    ...collectSkuIdsFromPlatformRes(nextPlatform),
  ])];
  const siteLabels = resolveSiteLabels(nextForm);
  const patched = {
    ...full,
    title: String(nextForm?.title || nextForm?.productName || full.title || '').trim() || full.title,
    supplierCode: firstSupplierCode(nextForm) || full.supplierCode || '',
    categoryLabel: String(nextForm?.categoryLabel || nextForm?.categoryPathLabel || full.categoryLabel || '').trim(),
    skcItems,
    productSkuIds,
    siteLabels: siteLabels.length ? siteLabels : (full.siteLabels || []),
    formSnapshot: nextForm && typeof nextForm === 'object' ? nextForm : (full.formSnapshot || {}),
    platformRaw: nextPlatform || full.platformRaw || null,
    updatedAt: nowBeijingOffsetIso(),
  };
  try {
    await idbPut(patched);
  } catch {
    try {
      localStorage.setItem(`ipe_temu_publish_record_body_${patched.id}`, JSON.stringify(patched));
    } catch {
      /* ignore */
    }
  }
  const index = readIndex(shopId).map((item) => (
    String(item.id) === String(patched.id) ? toListItem(patched) : item
  ));
  writeIndex(shopId, index);
  return toListItem(patched);
}

export async function getTemuLocalPublishRecord(shopId, recordId) {
  const id = String(recordId || '');
  if (!id) return null;
  let record = null;
  try {
    record = await idbGet(id);
  } catch {
    record = null;
  }
  if (!record) {
    try {
      const raw = localStorage.getItem(`ipe_temu_publish_record_body_${id}`);
      if (raw) record = JSON.parse(raw);
    } catch {
      record = null;
    }
  }
  if (!record || String(record.shopId) !== String(shopId)) return null;
  return record;
}

/** 按平台 SPU / productId 查是否已有本地发布记录（刊登任务回写去重） */
export async function findTemuLocalPublishRecordBySpu(shopId, publishedSpu) {
  const spu = String(publishedSpu || '').trim();
  if (!shopId || !spu) return null;
  const hit = readIndex(shopId).find((row) => {
    const id = String(row?.platformSpuName || row?.productId || '').trim();
    return id && id === spu;
  });
  if (!hit?.id) return null;
  return getTemuLocalPublishRecord(shopId, hit.id);
}

/**
 * @param {object} [options]
 * @param {Array} [options.reviewItems] 本地核价缓存，用于推导状态
 * @param {Array} [options.lifecycleItems] bg.glo.product.search 生命周期行
 */
export async function listTemuLocalPublishRecords(
  shopId,
  params = {},
  { reviewItems = [], lifecycleItems = [] } = {},
) {
  const page = Math.max(1, Number(params.page) || 1);
  const pageSize = Math.min(100, Math.max(1, Number(params.pageSize) || 20));
  const statusFilter = String(params.status || '').trim();
  const titleQ = String(params.title || '').trim().toLowerCase();
  const supplierQ = String(params.supplierCode || '').trim().toLowerCase();
  const keywords = String(params.keywords || '').trim().toLowerCase();
  const idType = String(params.idType || 'skc').trim();
  const keywordList = keywords
    ? keywords.split(/[,，\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean)
    : [];

  const skuStatusMap = buildSkuReviewStatusMap(reviewItems);
  const afterReview = readIndex(shopId).map((row) => applyDerivedPublishStatuses(row, skuStatusMap));
  const allDerived = applyLifecycleToPublishRecords(afterReview, lifecycleItems);

  // 旧记录补站点/供货价/平台 SKU：从 IDB formSnapshot + platformRaw 回填
  const withSitePrices = await Promise.all(allDerived.map(async (row) => {
    const skcList = Array.isArray(row.skcItems) ? row.skcItems : [];
    const hasLines = skcList.some((s) => Array.isArray(s?.sitePriceLines) && s.sitePriceLines.length);
    const missingSkuIds = skcList.some((s) => {
      const ids = Array.isArray(s?.productSkuIds) ? s.productSkuIds : [];
      const lines = Array.isArray(s?.sitePriceLines) ? s.sitePriceLines : [];
      if (!ids.length) return true;
      return lines.some((line) => {
        const label = String(line?.skuLabel || '').trim();
        return label && label !== 'SKU' && !line?.platformSkuId && !line?.productSkuId;
      });
    });
    if (hasLines && !missingSkuIds) {
      return {
        ...row,
        skcItems: skcList.map(attachPlatformSkuIdsToSitePriceLines),
      };
    }
    try {
      const full = await getTemuLocalPublishRecord(shopId, row.id);
      if (!full) return row;
      let nextSkc = Array.isArray(full.skcItems) && full.skcItems.length
        ? full.skcItems
        : skcList;
      if (full.platformRaw) {
        nextSkc = assignPlatformSkuIdsToSkcItems(nextSkc, full.formSnapshot || {}, full.platformRaw);
      }
      if (full.formSnapshot) {
        nextSkc = enrichSkcItemsWithSitePrices(nextSkc, full.formSnapshot);
      }
      nextSkc = nextSkc.map(attachPlatformSkuIdsToSitePriceLines);
      const nextProductSkuIds = [...new Set([
        ...(Array.isArray(full.productSkuIds) ? full.productSkuIds : []),
        ...nextSkc.flatMap((s) => s.productSkuIds || []),
      ])];
      // 持久化回填结果，避免每次列表都重算
      try {
        const patched = {
          ...full,
          skcItems: nextSkc,
          productSkuIds: nextProductSkuIds,
          siteLabels: full.formSnapshot
            ? resolveSiteLabels(full.formSnapshot)
            : (row.siteLabels || full.siteLabels || []),
        };
        await idbPut(patched);
        const index = readIndex(shopId).map((item) => (
          String(item.id) === String(row.id) ? toListItem(patched) : item
        ));
        writeIndex(shopId, index);
      } catch {
        /* ignore persist */
      }
      return {
        ...row,
        siteLabels: full.formSnapshot
          ? resolveSiteLabels(full.formSnapshot)
          : (row.siteLabels || []),
        productSkuIds: nextProductSkuIds,
        skcItems: nextSkc,
      };
    } catch {
      return row;
    }
  }));

  const statusCounts = {
    all: withSitePrices.length,
    [PUBLISH_RECORD_STATUS.PENDING_MERCHANT]: 0,
    [PUBLISH_RECORD_STATUS.PENDING_REVIEW]: 0,
    [PUBLISH_RECORD_STATUS.APPROVED]: 0,
    [PUBLISH_RECORD_STATUS.REJECTED]: 0,
  };
  withSitePrices.forEach((row) => {
    const s = String(row.statusLabel || row.status);
    if (Object.prototype.hasOwnProperty.call(statusCounts, s)) {
      statusCounts[s] += 1;
    }
  });

  let items = withSitePrices;
  if (statusFilter) {
    items = items.filter((row) => String(row.statusLabel || row.status) === statusFilter);
  }
  if (titleQ) {
    items = items.filter((row) => String(row.title || '').toLowerCase().includes(titleQ));
  }
  if (supplierQ) {
    items = items.filter((row) => {
      if (String(row.supplierCode || '').toLowerCase().includes(supplierQ)) return true;
      return (Array.isArray(row.skcItems) ? row.skcItems : []).some((skc) => (
        String(skc?.supplierCode || '').toLowerCase().includes(supplierQ)
      ));
    });
  }
  if (keywordList.length) {
    items = items.filter((row) => {
      const hay = idType === 'spu'
        ? String(row.platformSpuName || '').toLowerCase()
        : idType === 'sku'
          ? (row.productSkuIds || []).map(String).join(' ').toLowerCase()
          : (Array.isArray(row.skcItems) ? row.skcItems : [])
            .map((s) => String(s.skcName || '')).join(' ').toLowerCase();
      return keywordList.some((k) => hay.includes(k));
    });
  }

  const total = items.length;
  const start = (page - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);

  return {
    items: pageItems,
    content: pageItems,
    total,
    totalElements: total,
    page,
    pageSize,
    statusCounts,
    neverSynced: false,
  };
}

export async function batchDeleteTemuLocalPublishRecords(shopId, ids = []) {
  const idList = (Array.isArray(ids) ? ids : []).map(String).filter(Boolean);
  if (!idList.length) return { deleted: 0 };
  await idbDelete(idList);
  idList.forEach((id) => {
    try {
      localStorage.removeItem(`ipe_temu_publish_record_body_${id}`);
    } catch {
      /* ignore */
    }
  });
  const idSet = new Set(idList);
  writeIndex(shopId, readIndex(shopId).filter((row) => !idSet.has(String(row.id))));
  return { deleted: idList.length, ok: true };
}
